/**
 * Deduplicates identical GET requests (in-flight + short cache).
 * Prevents duplicate network calls from React StrictMode double mount in dev.
 */

const inflightGet = new Map<string, Promise<unknown>>();
const recentGet = new Map<string, { at: number; value: unknown }>();

/** How long to reuse a completed GET response (ms). */
const RECENT_GET_MS = 800;

export function dedupeGetRequest<T>(url: string, execute: () => Promise<T>): Promise<T> {
  const cached = recentGet.get(url);
  if (cached && Date.now() - cached.at < RECENT_GET_MS) {
    return Promise.resolve(cached.value as T);
  }

  const existing = inflightGet.get(url);
  if (existing) {
    return existing as Promise<T>;
  }

  const promise = execute()
    .then((value) => {
      recentGet.set(url, { at: Date.now(), value });
      return value;
    })
    .finally(() => {
      inflightGet.delete(url);
    });

  inflightGet.set(url, promise);
  return promise;
}

/** Clear cache after mutations so lists refresh on next navigation. */
export function clearGetRequestCache(): void {
  recentGet.clear();
  inflightGet.clear();
}
